import AddIcon from "@mui/icons-material/Add";
import { Box, Container, Dialog, Fab } from "@mui/material";
import Alert from "@mui/material/Alert";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SearchBoxPersisted from "components/SearchBoxPersisted";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useListTitle from "hooks/useListTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  GetQualificationsRequest,
  IQualification,
  ListResponseOfQualification,
  QualificationFilter,
} from "services/KnowfullyAdminClient";
import { Organizations } from "@knowfully/ui.common";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import { SnackbarMessageProps } from "types/common-types";
import QualificationForm from "./QualificationForm";

const QualificationList = () => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const { qualificationService, qualificationDetailsUrl } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("value");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [createOpen, createOpenActions] = useBoolean(false);
  const queryClient = useQueryClient();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const searchTerm = persistanceStore.useState(
    (s) => s.qualificationSearchTerm,
  );
  const { organizationId } = contextStore.useState();

  useListTitle("accreditationBodies");

  const listRequest = new GetQualificationsRequest({
    organizationId,
    pageNumber: page,
    pageSize: rowsPerPage,
    sortField: orderBy,
    sortDescending: order === "desc",
    parameters: QualificationFilter.fromJS({
      searchText: searchTerm,
    }),
  });

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfQualification>(
    ["qualificationService.getQualifications", listRequest],
    () => qualificationService.getQualifications(listRequest),
    {
      keepPreviousData: true,
    },
  );

  const isRootOrganization =
    organizationId === Organizations.KnowfullyLearningGroup;

  if (isRootOrganization) {
    return (
      <Alert severity="info">
        {t({ id: "rootOrganizationIsNotAllowedTooltip" })}
      </Alert>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const qualifications = data?.data as IQualification[];

  const cells: Array<TableCellType> = [
    {
      accessor: "value",
      align: "left",
      width: 1,
      type: "text",
      label: t({ id: "valueLabel" }),
    },
    {
      accessor: "acronym",
      align: "left",
      width: 1,
      type: "text",
      label: t({ id: "acronymLabel" }),
    },
    {
      accessor: "displayOrder",
      align: "left",
      width: 1,
      type: "text",
      label: t({ id: "displayOrderLabel" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(qualificationDetailsUrl(id));
  };

  const handleQualificationCreated = () => {
    queryClient.invalidateQueries(["qualificationService.getQualifications"]);
    queryClient.invalidateQueries(["qualification", organizationId]);
    createOpenActions.setFalse();

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "accreditationBodyCreated" })),
    );
  };

  const handleSetPersistedSearchTerm = (searchText: string): void => {
    persistanceStore.update((s) => {
      s.qualificationSearchTerm = searchText;
    });
  };

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Fab
          aria-label="add"
          color="primary"
          onClick={createOpenActions.setTrue}
        >
          <AddIcon />
        </Fab>
      </Box>

      <SearchBoxPersisted
        setPersistedSearchTerm={handleSetPersistedSearchTerm}
        persistedSearchTerm={searchTerm}
      />

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: qualifications ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "accreditationBodies" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) =>
              qualificationDetailsUrl(id),
            ),
          ],
        }}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <QualificationForm
          title={t({ id: "accreditationBodyFormAddTitle" })}
          onSubmit={handleQualificationCreated}
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default QualificationList;
